import type { ReactNode } from 'react'
import styles from './Label.module.css'

/**
 * QUIL — the annotation label. Design contract v3 "Colours" §4, Tier A anchor.
 *
 * The metadata voice: chapter status, trope beats, plot-line names, sync state,
 * word counts below display size. Most of Quil's screen is metadata about a
 * story, which is why this is the role with the widest reach in the app.
 *
 * It exists because the look was previously hand-rolled per surface — 21 modules
 * across nine different letter-spacing values — so there was no single place to
 * change it and no way to tell a deliberate variation from a typo.
 *
 * Never set in the display face: Jagot is decorative, speaks for Quil rather
 * than for the writer, and is unreadable at this size (contract §3, the type
 * ration).
 */

export type LabelTone = 'default' | 'ink' | 'lit'

export function Label({
  children,
  tone = 'default',
  dot,
  title,
  id,
  testid,
}: {
  children: ReactNode
  /** `ink` promotes it to a readable value; `lit` is the one lit element. */
  tone?: LabelTone
  /**
   * Device one: a status dot beside the word. Pass a CSS colour (normally a
   * token, e.g. `var(--ok)`). The dot NEVER replaces the text — contract §3
   * forbids colour as the sole carrier of state — so it is decorative to
   * assistive tech and the label's own words carry the meaning.
   */
  dot?: string
  title?: string
  id?: string
  testid?: string
}) {
  const toneClass = tone === 'ink' ? styles.ink : tone === 'lit' ? styles.lit : ''
  return (
    <span className={`${styles.label} ${toneClass}`} title={title} id={id} data-testid={testid}>
      {dot && <span className={styles.dot} style={{ background: dot }} aria-hidden="true" />}
      {children}
    </span>
  )
}
